import React, { useState, useEffect, useCallback } from 'react';
import { Check, Menu, X, ArrowRight } from 'lucide-react';
import Dashboard from "./Dashboard";
import AuthScreen from "./AuthScreen";
import Logo from "./Logo";
import { fetchUserSubscription, setGlobalAuthErrorHandler, clearGlobalAuthErrorHandler } from "../api";
import { getTokens as readTokens, setTokens as persistTokens } from "../../shared/utils/secureTokenStorage";
import PricingCalculator from "./PricingCalculator";

// Main LandingPage Component
const LandingPage = () => {
  const [currentView, setCurrentView] = useState("landing");
  const [isVisible, setIsVisible] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [email, setEmail] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const SESSION_KEY = "cm_user_session";

  // Animation trigger
  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Register global authentication error handler
  useEffect(() => {
    setGlobalAuthErrorHandler((error) => {
      console.error("Global authentication error detected:", error);
      handleLogout(true);
    });
    return () => {
      clearGlobalAuthErrorHandler();
    };
  }, []);

  // Session restoration
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const hasCheckoutFlag = searchParams.get("success") || searchParams.get("canceled");

    if (hasCheckoutFlag) {
      console.log("[Checkout Return] Detected checkout return, restoring auth state...");
      try {
        const currentTokens = readTokens();
        const backupTokens = window.localStorage.getItem("cm_auth_tokens_backup");
        if (!currentTokens && backupTokens) {
          persistTokens(JSON.parse(backupTokens), false);
          window.localStorage.removeItem("cm_auth_tokens_backup");
        }
      } catch (error) {
        console.error("[Checkout Return] Token restoration failed:", error);
      }

      const storedSession = localStorage.getItem(SESSION_KEY);
      try {
        const parsed = JSON.parse(storedSession);
        if (parsed?.email) {
          setUserEmail(parsed.email);
        }
      } catch {
        if (storedSession && storedSession.includes("@")) {
          setUserEmail(storedSession);
        }
      }
      setCurrentView("dashboard");
      return;
    }

    const storedSession = localStorage.getItem(SESSION_KEY);
    if (!storedSession) return;

    try {
      const parsed = JSON.parse(storedSession);
      const storedRole = parsed?.role ? String(parsed.role).toLowerCase() : "";
      const storedEmail = parsed?.email || "";

      if (storedRole === "borrower") {
        window.location.href = "/borrower";
        return;
      }

      if (storedRole === "lender") {
        setUserEmail(storedEmail);
        setCurrentView("dashboard");
        return;
      }
    } catch {
      // legacy string sessions
    }
    localStorage.removeItem(SESSION_KEY);
  }, []);

  const handleLoginSuccess = async (authPayload) => {
    console.log("[Auth] Login success:", authPayload);

    const email = typeof authPayload === "string" ? authPayload : authPayload?.email;
    const roleRaw = typeof authPayload === "object" ? authPayload?.role : undefined;
    const role = roleRaw ? String(roleRaw).toLowerCase() : undefined;
    const session = JSON.stringify({ email: email || "", role: role || "" });

    if (role === "borrower") {
      localStorage.setItem(SESSION_KEY, session);
      window.location.href = "/borrower";
      return;
    }

    if (role === "lender") {
      setUserEmail(email || "");
      setCurrentView("dashboard");
      localStorage.setItem(SESSION_KEY, session);

      try {
        const subscriptionResponse = await fetchUserSubscription();
        console.log("User Subscription Details:", subscriptionResponse.data);
      } catch (error) {
        console.error("Failed to fetch subscription:", error);
      }
    } else {
      console.warn("[Auth] Invalid role:", roleRaw);
      localStorage.removeItem(SESSION_KEY);
      setCurrentView("auth");
    }
  };

  const handleLogout = useCallback((silent) => {
    setUserEmail("");
    setCurrentView("landing");
    localStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_KEY);
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/";
  }, []);

  // View Routing
  if (currentView === 'auth') {
    return <AuthScreen onLogin={handleLoginSuccess} onBack={() => setCurrentView('landing')} />;
  }

  if (currentView === 'dashboard') {
    return <Dashboard userEmail={userEmail} onLogout={handleLogout} />;
  }

  const features = [
    {
      icon: '⚡',
      title: 'Save 15+ Hours Weekly',
      description: 'Automate listings, follow-ups, and paperwork. Focus on what matters—closing deals.',
      stat: '15+',
      statLabel: 'Hours Saved'
    },
    {
      icon: '🎯',
      title: 'Close More Deals',
      description: 'AI-powered insights predict buyer behavior and optimize your pitch timing.',
      stat: '3.2x',
      statLabel: 'More Closings'
    },
    {
      icon: '📈',
      title: 'Generate Quality Leads',
      description: 'Smart lead scoring and automated nurturing sequences that convert.',
      stat: '47%',
      statLabel: 'More Leads'
    }
  ];

  const pricingPlans = [
    {
      plan: "Founder Edition",
      price: "$0",
      highlighted: false,
      features: [
        { active: true, text: "Unlimited Cases" },
        { active: true, text: "Unlimited Documents Uploaded" },
        { active: true, text: "Borrower Portal" },
        { active: true, text: "Case Management Dashboard" },
        { active: true, text: "Executive Dashboard (P&L and Tasks)" },
        { active: true, text: "Task Management" },
        { active: true, text: "P&L Tracker" },
        { active: true, text: "Notes Management and Collaboration" },
        { active: true, text: "Team Chat: Join only" },
        { active: true, text: "5000 A.I credits included" },
        { active: true, text: "A.I Report (9-hour processing time)" },
        { active: true, text: "AI Assistant-Underwriting Intelligence Assistant" },
        { active: true, text: "AI Assistant-Meeting Assistant" }
      ]
    },
    {
      plan: "Starter",
      price: "$39.99",
      highlighted: true,
      features: [
        { active: true, text: "Unlimited Cases" },
        { active: true, text: "Unlimited Documents Uploaded" },
        { active: true, text: "Borrower Portal" },
        { active: true, text: "Case Management Dashboard" },
        { active: true, text: "Executive Dashboard (P&L and Tasks)" },
        { active: true, text: "Task Management" },
        { active: true, text: "P&L Tracker" },
        { active: true, text: "Notes Management and Collaboration" },
        { active: true, text: "Team Chat: Admin" },
        { active: true, text: "Team: 3 users" },
        { active: true, text: "10,000 A.I credits per month" },
        { active: true, text: "A.I Report (6-hour processing time)" },
        { active: true, text: "AI Assistant-Underwriting Intelligence Assistant" },
        { active: true, text: "AI Assistant-Meeting Assistant" }
      ]
    },
    {
      plan: "Pro",
      price: "$79.99",
      highlighted: false,
      features: [
        { active: true, text: "Unlimited Cases" },
        { active: true, text: "Unlimited Documents Uploaded" },
        { active: true, text: "Borrower Portal" },
        { active: true, text: "Case Management Dashboard" },
        { active: true, text: "Executive Dashboard (P&L and Tasks)" },
        { active: true, text: "Task Management" },
        { active: true, text: "P&L Tracker" },
        { active: true, text: "Notes Management and Collaboration" },
        { active: true, text: "Team Chat: Admin" },
        { active: true, text: "Team: 10 users" },
        { active: true, text: "20,000 A.I credits per month" },
        { active: true, text: "A.I Report (3-hour processing time)" },
        { active: true, text: "AI Assistant-Underwriting Intelligence Assistant" },
        { active: true, text: "AI Assistant-Meeting Assistant" }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0f1c] text-white overflow-hidden" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');

        .font-display { font-family: 'DM Sans', sans-serif; letter-spacing: -0.02em; }

        .gradient-text {
          color: #fbbf24;
          background: none;
          -webkit-text-fill-color: #fbbf24;
        }

        .glow-box {
          background: linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(251, 191, 36, 0.05) 100%);
          border: 1px solid rgba(251, 191, 36, 0.2);
          backdrop-filter: blur(20px);
        }

        .hero-glow {
          background: radial-gradient(ellipse 80% 50% at 50% -20%, rgba(251, 191, 36, 0.15) 0%, transparent 70%);
        }

        .floating {
          animation: float 6s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }

        .slide-up {
          animation: slideUp 0.8s ease-out forwards;
          opacity: 0;
          transform: translateY(40px);
        }

        @keyframes slideUp {
          to { opacity: 1; transform: translateY(0); }
        }

        .stagger-1 { animation-delay: 0.1s; }
        .stagger-2 { animation-delay: 0.2s; }
        .stagger-3 { animation-delay: 0.3s; }
        .stagger-4 { animation-delay: 0.4s; }
        .stagger-5 { animation-delay: 0.5s; }

        .grid-bg {
          background-image:
            linear-gradient(rgba(251, 191, 36, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(251, 191, 36, 0.03) 1px, transparent 1px);
          background-size: 60px 60px;
        }

        .card-hover {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .card-hover:hover {
          transform: translateY(-8px);
          border-color: rgba(251, 191, 36, 0.4);
          box-shadow: 0 20px 40px rgba(251, 191, 36, 0.1);
        }

        .btn-primary {
          background: #fbbf24;
          transition: all 0.3s ease;
        }

        .btn-primary:hover {
          transform: scale(1.02);
          background: #facc15;
          box-shadow: 0 10px 30px rgba(251, 191, 36, 0.3);
        }

        .feature-active {
          background: linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(251, 191, 36, 0.08) 100%);
          border-color: rgba(251, 191, 36, 0.5);
        }
      `}</style>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 px-6 py-4 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Logo />
          <div className="hidden md:flex items-center gap-8 text-sm text-gray-400">
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#testimonials" className="hover:text-white transition-colors">Testimonials</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
          </div>
          <div className="hidden md:flex items-center gap-4">
            <button onClick={() => setCurrentView('auth')} className="text-sm text-gray-400 hover:text-white transition-colors">Sign In</button>
            <button onClick={() => setCurrentView('auth')} className="btn-primary px-5 py-2.5 rounded-full text-sm font-semibold text-[#0a0f1c]">
              Get Started Free
            </button>
          </div>
          <button className="md:hidden text-white" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-[#0a0f1c] border-b border-gray-800 p-6 md:hidden animate-in slide-in-from-top-5">
            <div className="flex flex-col gap-4">
              {['Features', 'Testimonials', 'Pricing'].map(item => (
                <a key={item} href={`#${item.toLowerCase()}`} onClick={() => setIsMobileMenuOpen(false)} className="text-gray-400 hover:text-white">{item}</a>
              ))}
              <button onClick={() => { setCurrentView('auth'); setIsMobileMenuOpen(false); }} className="btn-primary w-full py-3 rounded-lg text-[#0a0f1c] font-bold mt-2">Get Started</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center hero-glow grid-bg pt-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#fbbf24] rounded-full opacity-5 blur-3xl floating"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#eab308] rounded-full opacity-5 blur-3xl floating" style={{ animationDelay: '-3s' }}></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <div className={`slide-up stagger-1 ${isVisible ? '' : 'opacity-0'}`}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glow-box mb-8">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
              <span className="text-sm text-gray-300">500+ agents already transforming their business</span>
            </div>
          </div>

          <h1 className={`font-display font-extrabold text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight mb-8 slide-up stagger-2 ${isVisible ? '' : 'opacity-0'}`}>
            <span className="block">Your AI-Powered</span>
            <span className="gradient-text">Real Estate Partner</span>
          </h1>

          <p className={`text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-12 leading-relaxed slide-up stagger-3 ${isVisible ? '' : 'opacity-0'}`}>
            Whether you buy, sell, or manage properties— We got your back.
            Powerful AI tools handle the busywork so you can focus on what actually grows your business: relationships, strategy, and closing.
          </p>

          <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 slide-up stagger-4 ${isVisible ? '' : 'opacity-0'}`}>
            <div className="flex items-center bg-[#141b2d] rounded-full p-1.5 border border-gray-800 w-full max-w-md sm:w-auto">
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-transparent px-5 py-3 text-white placeholder-gray-500 outline-none w-full sm:w-64"
              />
              <button onClick={() => setCurrentView('auth')} className="btn-primary px-6 py-3 rounded-full font-semibold text-[#0a0f1c] whitespace-nowrap">
                Start Free Trial
              </button>
            </div>
          </div>

          <p className={`text-sm text-gray-500 mt-4 slide-up stagger-5 ${isVisible ? '' : 'opacity-0'}`}>
            No credit card required • 14-day free trial • Cancel anytime
          </p>

          {/* Stats Row */}
          <div className={`grid grid-cols-3 gap-8 max-w-3xl mx-auto mt-20 slide-up stagger-5 ${isVisible ? '' : 'opacity-0'}`}>
            {[
              { value: '10,000+', label: 'Leads Generated' },
              { value: '$2.1B', label: 'Deal Volume' },
              { value: '98%', label: 'Satisfaction Rate' }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="font-display font-bold text-3xl md:text-4xl gradient-text">{stat.value}</div>
                <div className="text-sm text-gray-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - Product Showcase */}
      <section id="features" className="relative py-32 px-6 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 hero-glow opacity-50"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Section Header */}
          <div className="text-center mb-24">
            <span className="inline-block text-[#fbbf24] text-sm font-semibold tracking-wider uppercase mb-4 px-4 py-2 rounded-full bg-[#fbbf24]/10 border border-[#fbbf24]/20">
              Product Features
            </span>
            <h2 className="font-display font-bold text-4xl md:text-6xl mt-4 mb-6">
              Everything You Need to<br /><span className="gradient-text">Dominate Your Market</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Powerful AI tools designed specifically for real estate professionals who want to work smarter, not harder.
            </p>
          </div>

          {/* Feature 1 - AI Underwriting Report */}
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20 mb-32">
            <div className="flex-1 order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#fbbf24]/10 border border-[#fbbf24]/20 mb-6">
                <span className="text-[#fbbf24] text-xl">🤖</span>
                <span className="text-[#fbbf24] text-sm font-medium">AI-Powered</span>
              </div>
              <h3 className="font-display font-bold text-3xl md:text-4xl mb-6 text-white">
                AI Underwriting Report
              </h3>
              <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                Get comprehensive loan analysis in minutes, not hours. Our AI reviews documents, calculates DTI, verifies income, and flags potential issues automatically.
              </p>
              <ul className="space-y-4 mb-8">
                {['Automated document analysis', 'Income & asset verification', 'Risk assessment & flags', 'Compliance checking'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-300">
                    <div className="w-6 h-6 rounded-full bg-[#fbbf24]/20 flex items-center justify-center">
                      <Check className="w-3.5 h-3.5 text-[#fbbf24]" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <button onClick={() => setCurrentView('auth')} className="inline-flex items-center gap-2 text-[#fbbf24] font-semibold hover:gap-3 transition-all">
                Try AI Reports <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 order-1 lg:order-2">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-[#fbbf24]/20 to-[#eab308]/20 rounded-3xl blur-2xl"></div>
                <div className="relative glow-box rounded-2xl p-2 overflow-hidden">
                  <div className="bg-[#141b2d] rounded-xl aspect-video flex items-center justify-center">
                    <div className="text-center p-8">
                      <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#fbbf24]/20 flex items-center justify-center">
                        <svg className="w-10 h-10 text-[#fbbf24]" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                      <p className="text-gray-400 text-sm">Watch Demo Video</p>
                      <p className="text-gray-600 text-xs mt-1">AI Underwriting in Action</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 2 - Case Management Dashboard */}
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20 mb-32">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-l from-[#fbbf24]/20 to-[#eab308]/20 rounded-3xl blur-2xl"></div>
                <div className="relative glow-box rounded-2xl p-2 overflow-hidden">
                  <div className="bg-[#141b2d] rounded-xl aspect-video flex items-center justify-center overflow-hidden">
                    <img
                      src="/dashboard-preview.png"
                      alt="Dashboard Preview"
                      className="w-full h-full object-cover opacity-80"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden flex-col items-center justify-center p-8 text-center w-full h-full">
                      <div className="grid grid-cols-3 gap-3 mb-4 w-full max-w-sm">
                        {[1, 2, 3, 4, 5, 6].map(i => (
                          <div key={i} className="h-16 rounded-lg bg-[#1e293b] animate-pulse"></div>
                        ))}
                      </div>
                      <p className="text-gray-500 text-sm">Dashboard Screenshot</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#fbbf24]/10 border border-[#fbbf24]/20 mb-6">
                <span className="text-[#fbbf24] text-xl">📊</span>
                <span className="text-[#fbbf24] text-sm font-medium">All-in-One</span>
              </div>
              <h3 className="font-display font-bold text-3xl md:text-4xl mb-6 text-white">
                Case Management Dashboard
              </h3>
              <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                Track every loan from application to closing. Kanban boards, task management, document storage, and team collaboration all in one place.
              </p>
              <ul className="space-y-4 mb-8">
                {['Kanban pipeline view', 'Document management', 'Team collaboration', 'Progress tracking'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-300">
                    <div className="w-6 h-6 rounded-full bg-[#fbbf24]/20 flex items-center justify-center">
                      <Check className="w-3.5 h-3.5 text-[#fbbf24]" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <button onClick={() => setCurrentView('auth')} className="inline-flex items-center gap-2 text-[#fbbf24] font-semibold hover:gap-3 transition-all">
                Explore Dashboard <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Feature 3 - AI Meeting Assistant */}
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20 mb-32">
            <div className="flex-1 order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#fbbf24]/10 border border-[#fbbf24]/20 mb-6">
                <span className="text-[#fbbf24] text-xl">🎙️</span>
                <span className="text-[#fbbf24] text-sm font-medium">Smart Automation</span>
              </div>
              <h3 className="font-display font-bold text-3xl md:text-4xl mb-6 text-white">
                AI Meeting Assistant
              </h3>
              <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                Never miss a detail. You can send CloudMortgage.io Meeting Assistant to your meetings, and it will take notes, while you focus on closing the deal.
              </p>
              <ul className="space-y-4 mb-8">
                {['Real-time transcription', 'Action item extraction', 'Auto follow-up drafts', 'Meeting summaries'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-300">
                    <div className="w-6 h-6 rounded-full bg-[#fbbf24]/20 flex items-center justify-center">
                      <Check className="w-3.5 h-3.5 text-[#fbbf24]" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <button onClick={() => setCurrentView('auth')} className="inline-flex items-center gap-2 text-[#fbbf24] font-semibold hover:gap-3 transition-all">
                Meet Your Assistant Today <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 order-1 lg:order-2">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-[#fbbf24]/20 to-[#eab308]/20 rounded-3xl blur-2xl"></div>
                <div className="relative glow-box rounded-2xl p-2 overflow-hidden">
                  <div className="bg-[#141b2d] rounded-xl aspect-video flex items-center justify-center">
                    <div className="text-center p-8 w-full">
                      <div className="flex items-center justify-center gap-1 mb-6">
                        {[...Array(5)].map((_, i) => (
                          <div key={i} className="w-1.5 bg-[#fbbf24] rounded-full animate-pulse" style={{
                            height: `${20 + Math.random() * 30}px`,
                            animationDelay: `${i * 0.1}s`
                          }}></div>
                        ))}
                      </div>
                      <p className="text-gray-400 text-sm">Taking Notes...</p>
                      <div className="mt-4 bg-[#1e293b] rounded-lg p-3 text-left">
                        <p className="text-gray-500 text-xs mb-2">Meeting Notes</p>
                        <p className="text-gray-300 text-sm">"The team discussed the loan terms for the Smith property..."</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 4 - Borrower Portal */}
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-l from-[#fbbf24]/20 to-[#eab308]/20 rounded-3xl blur-2xl"></div>
                <div className="relative glow-box rounded-2xl p-2 overflow-hidden">
                  <div className="bg-[#141b2d] rounded-xl aspect-video flex items-center justify-center">
                    <div className="p-8 w-full">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#fbbf24] to-[#eab308] flex items-center justify-center text-[#0a0f1c] font-bold">
                          JD
                        </div>
                        <div>
                          <p className="text-white font-medium">John Doe</p>
                          <p className="text-gray-500 text-sm">Loan #2024-001</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Application Progress</span>
                          <span className="text-[#fbbf24]">75%</span>
                        </div>
                        <div className="h-2 bg-[#1e293b] rounded-full overflow-hidden">
                          <div className="h-full w-3/4 bg-gradient-to-r from-[#fbbf24] to-[#eab308] rounded-full"></div>
                        </div>
                        <div className="grid grid-cols-4 gap-2 mt-4">
                          {['Applied', 'Docs', 'Review', 'Closing'].map((step, i) => (
                            <div key={i} className={`text-center text-xs ${i < 3 ? 'text-[#fbbf24]' : 'text-gray-500'}`}>
                              {step}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#fbbf24]/10 border border-[#fbbf24]/20 mb-6">
                <span className="text-[#fbbf24] text-xl">👥</span>
                <span className="text-[#fbbf24] text-sm font-medium">Client Experience</span>
              </div>
              <h3 className="font-display font-bold text-3xl md:text-4xl mb-6 text-white">
                Borrower Portal
              </h3>
              <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                Give your clients a premium experience. They can upload documents, track progress, and communicate with you—all from a branded portal.
              </p>
              <ul className="space-y-4 mb-8">
                {['Secure document upload', 'Real-time status updates', 'Integrated messaging', 'Mobile-friendly'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-300">
                    <div className="w-6 h-6 rounded-full bg-[#fbbf24]/20 flex items-center justify-center">
                      <Check className="w-3.5 h-3.5 text-[#fbbf24]" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <button onClick={() => setCurrentView('auth')} className="inline-flex items-center gap-2 text-[#fbbf24] font-semibold hover:gap-3 transition-all">
                See Borrower Portal <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-32">
            {[
              { icon: '⚡', value: '15+', label: 'Hours Saved Weekly' },
              { icon: '🎯', value: '3.2x', label: 'More Closings' },
              { icon: '📈', value: '47%', label: 'More Leads' },
              { icon: '⏱️', value: '90%', label: 'Faster Processing' }
            ].map((stat, i) => (
              <div key={i} className="glow-box rounded-2xl p-6 text-center card-hover">
                <div className="text-3xl mb-3">{stat.icon}</div>
                <div className="font-display font-bold text-3xl gradient-text mb-1">{stat.value}</div>
                <div className="text-gray-500 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Calculator */}
      <PricingCalculator />

      {/* Pricing */}
      <section id="pricing" className="relative py-32 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#fbbf24] text-sm font-semibold tracking-wider uppercase">Pricing</span>
            <h2 className="font-display font-bold text-4xl md:text-5xl mt-4 mb-6">
              Simple, <span className="gradient-text">Transparent</span> Pricing
            </h2>
            <p className="text-gray-400">Choose the plan that fits your volume</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {pricingPlans.map((plan, i) => (
              <div key={i} className={`rounded-2xl p-8 flex flex-col ${plan.highlighted ? 'bg-gradient-to-b from-[#fbbf24]/20 to-[#eab308]/5 border-2 border-[#fbbf24]/50 scale-105 shadow-2xl shadow-[#fbbf24]/10' : 'glow-box'}`}>
                {plan.highlighted && (
                  <div className="text-xs font-bold text-[#fbbf24] uppercase tracking-wider mb-4">Most Popular</div>
                )}
                <h3 className="font-display font-bold text-2xl mb-2 text-white">{plan.plan}</h3>
                <div className="mb-6">
                  <span className="font-display font-bold text-5xl text-white">{plan.price}</span>
                  <span className="text-gray-500 ml-1 font-normal">/mo</span>
                </div>
                <ul className="space-y-4 mb-8 flex-1">
                  {plan.features.map((feat, j) => (
                    <li key={j} className={`flex items-start gap-3 text-sm ${feat.active ? "text-gray-300" : "text-gray-600 opacity-60"}`}>
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${feat.active ? "bg-[#fbbf24]/20 text-[#fbbf24]" : "bg-gray-800 text-gray-600"}`}>
                        {feat.active ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      </div>
                      <span className={feat.active ? "font-medium" : ""}>{feat.text}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => setCurrentView('auth')} className={`w-full py-4 rounded-full font-semibold transition-all ${plan.highlighted ? 'btn-primary text-[#0a0f1c]' : 'border border-gray-700 text-white hover:border-[#fbbf24]/50 hover:bg-[#fbbf24]/10'}`}>
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-800/50 py-12 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-500 text-sm">© 2026 CloudRealtor.io. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
